// src/Website/Components/HeroAnimations.jsx
//
// Distinct, characterful Hero visuals for the four product pages, rendered
// inside the shared ProductPage hero tile:
//
//   robot    → futuristic levitating droid + holographic match chips   (Jobs)
//   terminal → typing shell driving a live kanban pipeline      (Recruit)
//   mockup   → animated screening funnel with flowing dots      (ATS)
//   dataviz  → live analytics cockpit (donut + sparkline + KPIs)(HRMS)
//
// All variants:
//   • use the product Accent_Color + design tokens
//   • respect prefers-reduced-motion (static fallback)
//   • stay within the square tile bounds (fixed heights, no layout shift)
//   • use only framer-motion + CSS (no new dependencies)

import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { colors as DS } from "../../design-system";

/* ───────────────────────── shared helpers ───────────────────────── */
function useLoopCounter(target, reduce, { duration = 1600, hold = 1800 } = {}) {
  const [val, setVal] = useState(reduce ? target : 0);
  const raf = useRef();
  useEffect(() => {
    if (reduce) {
      setVal(target);
      return;
    }
    let start;
    let holdTimer;
    const animate = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * target));
      if (p < 1) raf.current = requestAnimationFrame(animate);
      else
        holdTimer = setTimeout(() => {
          start = undefined;
          raf.current = requestAnimationFrame(animate);
        }, hold);
    };
    raf.current = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(raf.current);
      clearTimeout(holdTimer);
    };
  }, [target, reduce, duration, hold]);
  return val;
}

/* ───────────────────── FUTURISTIC DROID (Jobs) ─────────────────────
   A sleek levitating AI droid: helmet head with a HUD visor + scanning
   eyes, a signal antenna, a rotating energy halo, and a hovering pod body
   (anti-gravity, no limbs), wrapped in holographic candidate-match chips. */
function FuturisticDroid({ accent, reduce }) {
  const chips = [
    { label: "React Engineer", score: "96%", x: "-4%", y: "12%", d: 0 },
    { label: "Product Lead", score: "91%", x: "70%", y: "26%", d: 1.1 },
    { label: "UX Designer", score: "88%", x: "2%", y: "72%", d: 2.2 },
  ];

  return (
    <div className="relative w-full max-w-[300px] aspect-square mx-auto">
      {/* holographic match chips */}
      {chips.map((c, i) => (
        <motion.div
          key={i}
          className="absolute z-20 flex items-center gap-1.5 px-2 py-1 border backdrop-blur-sm rounded-sm"
          style={{
            left: c.x, top: c.y,
            borderColor: `${accent}55`,
            background: "rgba(247,247,245,0.88)",
            boxShadow: `0 4px 16px ${accent}1f`,
          }}
          initial={reduce ? false : { opacity: 0, y: 6 }}
          animate={reduce ? { opacity: 1 } : { opacity: [0, 1, 1, 0.85], y: [6, -4, 0, -6] }}
          transition={reduce ? {} : { duration: 5, repeat: Infinity, delay: c.d, ease: "easeInOut" }}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
          <span className="text-[9px] font-bold whitespace-nowrap" style={{ color: DS.forest }}>{c.label}</span>
          <span className="text-[9px] font-bold ds-tabular" style={{ color: accent }}>{c.score}</span>
        </motion.div>
      ))}

      {/* anti-gravity floor glow */}
      <motion.span
        aria-hidden="true"
        className="absolute left-1/2 -translate-x-1/2 rounded-[50%]"
        style={{ bottom: "8%", width: 110, height: 16, background: accent, opacity: 0.18, filter: "blur(7px)" }}
        animate={reduce ? {} : { scaleX: [1, 0.8, 1], opacity: [0.18, 0.08, 0.18] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* levitating droid */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center"
        animate={reduce ? {} : { y: [0, -12, 0] }}
        transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
      >
        <svg viewBox="0 0 200 230" role="img" aria-label="Futuristic AI droid matching candidates to roles" className="w-[66%] h-[66%]">
          <defs>
            <linearGradient id="dHelmet" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#27543C" />
              <stop offset="100%" stopColor={DS.forest} />
            </linearGradient>
            <linearGradient id="dVisor" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0B1A12" />
              <stop offset="100%" stopColor="#13261B" />
            </linearGradient>
            <linearGradient id="dPod" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#27543C" />
              <stop offset="100%" stopColor={DS.forest} />
            </linearGradient>
            <radialGradient id="dAura" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={accent} stopOpacity="0.35" />
              <stop offset="100%" stopColor={accent} stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* ambient aura */}
          <motion.circle
            cx="100" cy="78" r="70" fill="url(#dAura)"
            style={{ transformBox: "fill-box", transformOrigin: "center" }}
            animate={reduce ? {} : { opacity: [0.7, 1, 0.7], scale: [1, 1.06, 1] }}
            transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
          />

          {/* rotating energy halo behind head */}
          <motion.ellipse
            cx="100" cy="80" rx="58" ry="20"
            fill="none" stroke={`${accent}66`} strokeWidth="1.5" strokeDasharray="3 7"
            style={{ transformBox: "fill-box", transformOrigin: "center" }}
            animate={reduce ? {} : { rotate: 360 }}
            transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
          />

          {/* antenna + signal */}
          <line x1="100" y1="34" x2="100" y2="16" stroke={DS.forest} strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="100" cy="13" r="4" fill={accent} />
          {!reduce && [0, 1].map((n) => (
            <motion.circle
              key={n}
              cx="100" cy="13" fill="none" stroke={accent} strokeWidth="1.5"
              animate={{ r: [2, 12], opacity: [0.8, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, delay: n * 0.9, ease: "easeOut" }}
            />
          ))}

          {/* head / helmet */}
          <rect x="56" y="40" width="88" height="76" rx="30" fill="url(#dHelmet)" />
          {/* top highlight */}
          <rect x="70" y="46" width="60" height="10" rx="5" fill={DS.paper} opacity="0.12" />
          {/* side ear pods */}
          <circle cx="56" cy="80" r="6" fill={DS.forest} />
          <motion.circle cx="56" cy="80" r="2.5" fill={accent}
            animate={reduce ? {} : { opacity: [1, 0.3, 1] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }} />
          <circle cx="144" cy="80" r="6" fill={DS.forest} />
          <motion.circle cx="144" cy="80" r="2.5" fill={accent}
            animate={reduce ? {} : { opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }} />

          {/* HUD visor */}
          <rect x="68" y="58" width="64" height="40" rx="16" fill="url(#dVisor)" stroke={`${accent}40`} strokeWidth="1" />
          {/* visor scan line */}
          {!reduce && (
            <motion.rect
              x="70" width="60" height="3" rx="1.5" fill={accent} opacity="0.5"
              animate={{ y: [62, 92, 62] }}
              transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
            />
          )}
          {/* scanning eyes */}
          <motion.g
            animate={reduce ? {} : { x: [-4, 4, -4] }}
            transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
          >
            <motion.g style={{ transformBox: "fill-box", transformOrigin: "center" }}
              animate={reduce ? {} : { scaleY: [1, 1, 0.1, 1, 1] }}
              transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 3, ease: "easeInOut" }}>
              <circle cx="86" cy="78" r="6" fill={accent} />
              <circle cx="114" cy="78" r="6" fill={accent} />
              <circle cx="86" cy="78" r="2.5" fill={DS.paper} opacity="0.9" />
              <circle cx="114" cy="78" r="2.5" fill={DS.paper} opacity="0.9" />
            </motion.g>
          </motion.g>

          {/* neck connector (floating gap below) */}
          <rect x="94" y="116" width="12" height="10" rx="3" fill={DS.forest} opacity="0.7" />

          {/* hovering pod body (anti-gravity, detached) */}
          <motion.g
            animate={reduce ? {} : { y: [0, 5, 0] }}
            transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
          >
            <path d="M60 150 Q100 128 140 150 Q150 172 100 184 Q50 172 60 150 Z" fill="url(#dPod)" />
            {/* core slit */}
            <rect x="80" y="150" width="40" height="8" rx="4" fill="#0B1A12" />
            <motion.rect x="84" y="152" width="32" height="4" rx="2" fill={accent}
              animate={reduce ? {} : { opacity: [0.4, 1, 0.4], width: [32, 22, 32] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }} />
            {/* thruster glow */}
            <motion.ellipse cx="100" cy="186" rx="16" ry="4" fill={accent}
              animate={reduce ? {} : { opacity: [0.5, 0.15, 0.5], ry: [4, 2, 4] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }} />
          </motion.g>
        </svg>
      </motion.div>

      {/* status caption */}
      <div className="absolute bottom-[1%] left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-20">
        <motion.span
          className="w-1.5 h-1.5 rounded-full" style={{ background: accent }}
          animate={reduce ? {} : { opacity: [1, 0.3, 1] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
        />
        <span className="text-[8px] uppercase tracking-[0.22em] font-bold" style={{ color: `${DS.grid}99` }}>
          ai online
        </span>
      </div>
    </div>
  );
}

/* ─────────────────── TERMINAL + KANBAN (Recruit) ─────────────────── */
function PipelineTerminal({ accent, reduce, lines }) {
  const script = useMemo(() => lines.join("\n"), [lines]);
  const [count, setCount] = useState(reduce ? script.length : 0);

  useEffect(() => {
    if (reduce) { setCount(script.length); return; }
    let i = 0;
    let timer;
    const step = () => {
      if (i >= script.length) {
        timer = setTimeout(() => { i = 0; setCount(0); timer = setTimeout(step, 600); }, 2000);
        return;
      }
      i += 1;
      setCount(i);
      const ch = script[i - 1];
      timer = setTimeout(step, ch === "\n" ? 240 : 24 + Math.random() * 30);
    };
    timer = setTimeout(step, 500);
    return () => clearTimeout(timer);
  }, [reduce, script]);

  const shownLines = script.slice(0, count).split("\n");
  const cols = ["Sourced", "Screen", "Offer"];

  return (
    <div className="w-full max-w-[330px] mx-auto border" style={{ borderColor: DS.hairline, background: DS.paper }}>
      {/* terminal */}
      <div className="border-b" style={{ borderColor: DS.hairline, background: "rgba(26,60,43,0.04)" }}>
        <div className="flex items-center gap-1.5 px-3 py-1.5 border-b" style={{ borderColor: DS.hairline }}>
          <span className="w-2 h-2 rounded-full" style={{ background: accent }} />
          <span className="w-2 h-2 rounded-full" style={{ background: `${DS.grid}55` }} />
          <span className="w-2 h-2 rounded-full" style={{ background: `${DS.grid}33` }} />
          <span className="ml-2 text-[9px] uppercase tracking-[0.18em] font-bold" style={{ color: `${DS.grid}99` }}>recruit · shell</span>
        </div>
        <div className="px-3 py-2 text-[10.5px] leading-[1.6] whitespace-pre-wrap break-words"
          style={{ color: DS.forest, height: 86, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
          {shownLines.map((ln, i) => (
            <div key={i} style={{ color: ln.startsWith("$") ? accent : DS.grid }}>
              {ln}
              {i === shownLines.length - 1 && (
                <motion.span aria-hidden="true" className="inline-block w-[6px] h-[11px] align-middle ml-0.5"
                  style={{ background: accent }} animate={reduce ? {} : { opacity: [1, 0, 1] }}
                  transition={{ duration: 0.9, repeat: Infinity, ease: "linear" }} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* live kanban */}
      <div className="relative grid grid-cols-3 gap-px p-px" style={{ background: DS.hairline, height: 150 }}>
        {cols.map((c, ci) => (
          <div key={c} className="px-2 py-2" style={{ background: DS.paper }}>
            <div className="text-[8px] uppercase tracking-[0.16em] font-bold mb-2" style={{ color: `${DS.grid}99` }}>{c}</div>
            {/* static placeholder cards */}
            {[0, 1].map((k) => (
              <div key={k} className="mb-1.5 rounded-sm" style={{ height: 18, background: `${DS.grid}12` }} />
            ))}
          </div>
        ))}

        {/* traveling candidate cards moving across stages */}
        {!reduce && [0, 1].map((n) => (
          <motion.div
            key={n}
            className="absolute rounded-sm flex items-center gap-1 px-1.5"
            style={{ height: 18, width: "28%", background: DS.paper, border: `1px solid ${accent}`, top: 30 + n * 24 }}
            animate={{
              left: ["3%", "3%", "36%", "36%", "69%", "69%"],
              opacity: [0, 1, 1, 1, 1, 0],
            }}
            transition={{ duration: 6, repeat: Infinity, delay: n * 2.4, ease: "easeInOut", times: [0, 0.12, 0.4, 0.55, 0.85, 1] }}
          >
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: accent }} />
            <span className="h-1.5 rounded-sm flex-1" style={{ background: `${DS.grid}33` }} />
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────── SCREENING FUNNEL (ATS) ─────────────────── */
function ScreeningFunnel({ accent, reduce }) {
  const stages = [
    { label: "Applied", w: 100, target: 1240 },
    { label: "Screened", w: 70, target: 312 },
    { label: "Interview", w: 44, target: 64 },
    { label: "Offer", w: 24, target: 12 },
  ];
  const c0 = useLoopCounter(stages[0].target, reduce);
  const c1 = useLoopCounter(stages[1].target, reduce);
  const c2 = useLoopCounter(stages[2].target, reduce);
  const c3 = useLoopCounter(stages[3].target, reduce);
  const counts = [c0, c1, c2, c3];

  return (
    <div className="relative w-full max-w-[300px] mx-auto border p-4" style={{ borderColor: DS.hairline, background: DS.paper, height: 300 }}>
      <div className="text-[9px] uppercase tracking-[0.18em] font-bold mb-3" style={{ color: `${DS.grid}99` }}>
        hiring funnel · auto-screen
      </div>

      {/* flowing applicant dots down the center */}
      {!reduce && [0, 1, 2, 3, 4].map((n) => (
        <motion.span
          key={n}
          aria-hidden="true"
          className="absolute left-1/2 w-1.5 h-1.5 rounded-full"
          style={{ background: accent, marginLeft: -3 }}
          animate={{ top: [40, 250], opacity: [0, 1, 1, 0] }}
          transition={{ duration: 2.6, repeat: Infinity, delay: n * 0.5, ease: "easeIn", times: [0, 0.1, 0.8, 1] }}
        />
      ))}

      <div className="relative z-10 flex flex-col items-center gap-2 mt-1">
        {stages.map((s, i) => (
          <div key={s.label} className="w-full flex flex-col items-center">
            <motion.div
              className="relative flex items-center justify-between px-3 rounded-sm overflow-hidden"
              style={{ width: `${s.w}%`, height: 40, background: `${accent}${i === 0 ? "26" : i === 1 ? "33" : i === 2 ? "55" : "FF"}` }}
              initial={reduce ? false : { scaleX: 0.6, opacity: 0 }}
              animate={{ scaleX: 1, opacity: 1 }}
              transition={{ delay: reduce ? 0 : i * 0.25, duration: 0.5, ease: "easeOut" }}
            >
              <span className="text-[10px] font-bold z-10" style={{ color: i >= 3 ? DS.paper : DS.forest }}>{s.label}</span>
              <span className="text-[11px] font-extrabold ds-tabular z-10" style={{ color: i >= 3 ? DS.paper : DS.forest }}>
                {counts[i].toLocaleString()}
              </span>
            </motion.div>
            {i < stages.length - 1 && (
              <svg width="14" height="10" viewBox="0 0 14 10" className="my-0.5" aria-hidden="true">
                <path d="M2 2 L7 7 L12 2" fill="none" stroke={`${DS.grid}66`} strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────── ANALYTICS COCKPIT (HRMS) ─────────────────── */
function AnalyticsCockpit({ accent, reduce }) {
  const ring = 0.78;
  const R = 40;
  const circ = 2 * Math.PI * R;
  const pct = useLoopCounter(78, reduce);
  const retention = useLoopCounter(94, reduce);
  const headcount = useLoopCounter(1284, reduce);

  // sparkline points
  const pts = [6, 12, 9, 18, 14, 24, 20, 30];
  const path = pts
    .map((v, i) => `${(i / (pts.length - 1)) * 100},${36 - (v / 30) * 30}`)
    .join(" ");

  return (
    <div className="w-full max-w-[320px] mx-auto border p-4 grid grid-cols-2 gap-3" style={{ borderColor: DS.hairline, background: DS.paper }}>
      {/* donut */}
      <div className="flex flex-col items-center justify-center border p-2" style={{ borderColor: DS.hairline }}>
        <div className="relative w-[84px] h-[84px]">
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
            <circle cx="50" cy="50" r={R} fill="none" stroke={`${DS.grid}22`} strokeWidth="10" />
            <motion.circle
              cx="50" cy="50" r={R} fill="none" stroke={accent} strokeWidth="10" strokeLinecap="round"
              strokeDasharray={circ}
              initial={reduce ? false : { strokeDashoffset: circ }}
              animate={{ strokeDashoffset: circ * (1 - ring) }}
              transition={reduce ? {} : { duration: 1.6, ease: "easeOut", repeat: Infinity, repeatType: "reverse", repeatDelay: 1.4 }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center text-base font-extrabold ds-tabular" style={{ color: DS.forest }}>
            {pct}%
          </div>
        </div>
        <span className="text-[8px] uppercase tracking-[0.16em] font-bold mt-1" style={{ color: `${DS.grid}99` }}>engagement</span>
      </div>

      {/* KPI tiles */}
      <div className="flex flex-col gap-3">
        <div className="border p-2" style={{ borderColor: DS.hairline }}>
          <div className="text-[8px] uppercase tracking-[0.16em] font-bold" style={{ color: `${DS.grid}99` }}>headcount</div>
          <div className="text-lg font-extrabold ds-tabular" style={{ color: DS.forest }}>{headcount.toLocaleString()}</div>
        </div>
        <div className="border p-2" style={{ borderColor: DS.hairline }}>
          <div className="text-[8px] uppercase tracking-[0.16em] font-bold" style={{ color: `${DS.grid}99` }}>retention</div>
          <div className="text-lg font-extrabold ds-tabular" style={{ color: accent }}>{retention}%</div>
        </div>
      </div>

      {/* self-drawing sparkline */}
      <div className="col-span-2 border p-2" style={{ borderColor: DS.hairline }}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[8px] uppercase tracking-[0.16em] font-bold" style={{ color: `${DS.grid}99` }}>productivity · 8wk</span>
          <span className="text-[9px] font-bold" style={{ color: accent }}>▲ 18%</span>
        </div>
        <svg viewBox="0 0 100 38" className="w-full h-10" preserveAspectRatio="none">
          <motion.polyline
            points={path} fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            initial={reduce ? false : { pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={reduce ? {} : { duration: 2, ease: "easeInOut", repeat: Infinity, repeatType: "reverse", repeatDelay: 1 }}
          />
        </svg>
      </div>
    </div>
  );
}

/* ───────────────────────── DISPATCHER ───────────────────────── */
const DEFAULT_TERMINAL_LINES = [
  "$ recruit source --role 'Senior Engineer'",
  "» scanning 12,480 candidates",
  "✓ 38 strong matches · shortlisting",
];

export default function HeroVisual({ variant = "icon", accent = DS.mint, Icon, terminalLines }) {
  const reduce = useReducedMotion();

  switch (variant) {
    case "robot":
      return <FuturisticDroid accent={accent} reduce={reduce} />;
    case "terminal":
      return (
        <PipelineTerminal
          accent={accent}
          reduce={reduce}
          lines={terminalLines && terminalLines.length ? terminalLines : DEFAULT_TERMINAL_LINES}
        />
      );
    case "mockup":
      return <ScreeningFunnel accent={accent} reduce={reduce} />;
    case "dataviz":
      return <AnalyticsCockpit accent={accent} reduce={reduce} />;
    default:
      return (
        <div className="relative flex items-center justify-center" style={{ width: 180, height: 180 }}>
          <motion.span aria-hidden="true" className="absolute rounded-full"
            style={{ width: 150, height: 150, background: accent, opacity: 0.12 }}
            animate={reduce ? {} : { scale: [1, 1.15, 1], opacity: [0.12, 0.04, 0.12] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }} />
          <div className="relative w-24 h-24 flex items-center justify-center" style={{ background: DS.forest, color: DS.paper }}>
            {Icon && <Icon className="w-10 h-10" strokeWidth={1.5} />}
          </div>
        </div>
      );
  }
}
